import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputElement} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {BoxedInputIcon} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputIcon';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InputTypesEnum} from '../../../../libs/shared/ui/src/components/inputs/input';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ScrollDirectionsEnum, ScrollerMoreStatuses} from '../../../../srcCommon/scroll/scrollerTypes';
import {Dialog} from '../../../components/common/dialogs/dialog';
import {DialogHeader} from '../../../components/common/dialogs/dialogHeader';
import {DropdownEmpty} from '../../../components/common/dropdowns/dropdownEmpty';
import {VirtualScroller} from '../../../components/common/scroll/virtualScroller';
import {AsyncMoreStatusesEnum} from '../../../store/appTypes';

/*
 * Props.
 */

interface SettingsResourceListDialogProps<T> {
  onRequestClose: Handler;
  title: ReactNode;
  searchQuery: string;
  onChange: HandlerOf<string>;
  topline?: ReactNode;
  onHeaderActionClick?: Handler;
  headerActionLabel?: ReactNode;
  onConfirmClick?: Handler;
  confirmLabel?: ReactNode;
  itemIds: ReadonlyArray<T>;
  emptyImageSource?: string;
  emptyLabel?: ReactNode;
  onLoadMore?: Handler;
  renderItem: RendererOf<T>;
  moreStatuses?: ScrollerMoreStatuses;
}

/*
 * Style.
 */

const StyledDialogContentDiv = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StyledBoxedInputIcon = styled(BoxedInputIcon)`
  flex: 0 0 16px;
  margin: 0 -4px 0 8px;
  height: 16px;
  padding: 2px;
`;

const StyledListHeader = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
  margin-right: 10px;
  margin-bottom: 2px;
`;

const StyledToplineWrapperDiv = styled.div`
  margin-top: 4px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 26px;
`;

const StyledDialogButtonGroup = styled.div`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  display: flex;
  justify-content: flex-end;

  padding: 20px 14px;

  & > * {
    margin: 0 5px;
  }
`;

/*
 * Component.
 */

export const SettingsResourceListDialog = <T,>(props: SettingsResourceListDialogProps<T>): ReturnType<FC> => {
  const theme = useUserTheme();
  const renderContent = () => {
    if (props.itemIds.length === 0) {
      return <DropdownEmpty imageSource={props.emptyImageSource}>{props.emptyLabel}</DropdownEmpty>;
    }

    return (
      <VirtualScroller
        moreStatuses={
          props.moreStatuses || {
            [ScrollDirectionsEnum.BOTTOM]: AsyncMoreStatusesEnum.ALL_LOADED,
            [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
          }
        }
        itemHeight={48}
        itemIds={props.itemIds}
        onLoadMore={(direction) => {
          if (!props.onLoadMore) {
            return;
          }

          if (direction !== ScrollDirectionsEnum.BOTTOM) {
            return;
          }

          props.onLoadMore();
        }}
        loaderHeight={1}
        renderLoader={() => null}
        renderItem={(id) => props.renderItem(id)}
      />
    );
  };

  return (
    <Dialog width={420} height={580} onRequestClose={props.onRequestClose}>
      <DialogHeader title={props.title} />
      <StyledDialogContentDiv>
        <BoxedInput
          noMargin
          boxStyles={{
            margin: '20px',
            marginBottom: '13px',
            alignItems: 'center',
          }}
        >
          {(childProps) => (
            <>
              <StyledBoxedInputIcon {...childProps} iconName="searchSmall" color={theme.greys.shade70} />
              <BoxedInputElement
                {...childProps}
                value={props.searchQuery}
                onChange={(newQuery) => {
                  props.onChange(newQuery);
                }}
                type={InputTypesEnum.TEXT}
                size={VisualSizesEnum.MEDIUM}
                shouldFocus
                placeholder="Search"
              />
            </>
          )}
        </BoxedInput>
        <StyledListHeader>
          {props.topline && <StyledToplineWrapperDiv>{props.topline}</StyledToplineWrapperDiv>}

          {props.onHeaderActionClick && props.headerActionLabel && (
            <Button
              size={VisualSizesEnum.SMALL}
              variant={ButtonVariantsEnum.LINK}
              onClick={props.onHeaderActionClick}
            >
              {props.headerActionLabel}
            </Button>
          )}
        </StyledListHeader>
        {renderContent()}
      </StyledDialogContentDiv>
      {props.onConfirmClick && props.confirmLabel && (
        <StyledDialogButtonGroup>
          <Button onClick={props.onRequestClose}>
            <Trans id="dialog-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={props.onConfirmClick}>
            {props.confirmLabel}
          </Button>
        </StyledDialogButtonGroup>
      )}
    </Dialog>
  );
};
